import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ShieldCheck, ShieldOff, Loader2, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import TwoFactorEnrollDialog from './TwoFactorEnrollDialog';
import { getTwoFactorStatus, disableTwoFactor } from '@/services/twoFactorService';
import { useLanguage } from '@/contexts/LanguageContext';


const TwoFactorSection = () => {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const load = async () => {
    try {
      const s = await getTwoFactorStatus();
      setEnabled(!!s.enabled);
    } catch {
      setEnabled(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDisable = async () => {
    setLoading(true);
    try {
      await disableTwoFactor({ code: disableCode });
      setEnabled(false);
      setDisableOpen(false);
      setDisableCode('');
      toast({ title: 'Double authentification désactivée' });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Erreur', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-medium">{t('login.twoFaTitle')}</h3>
              {enabled === true && (
                <Badge className="bg-green-600 text-white hover:bg-green-600">{t('login.twoFAEnabled')}</Badge>
              )}
              {enabled === false && <Badge variant="secondary">{t('login.twoFADisabled')}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-prose">
              {t('login.twoFaDescription')}
            </p>
          </div>
        </div>

        <div className="shrink-0">
          {enabled === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : enabled ? (
            <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setDisableOpen(true)}>
              <ShieldOff className="h-4 w-4 mr-2" /> {t('login.desactivate2FABtn')}
            </Button>
          ) : (
            <Button onClick={() => setEnrollOpen(true)}>
              <ShieldCheck className="h-4 w-4 mr-2" /> {t('login.activate2FABtn')}
            </Button>
          )}
        </div>
      </div>

      <TwoFactorEnrollDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        onEnrolled={() => setEnabled(true)}
      />

      <AlertDialog open={disableOpen} onOpenChange={setDisableOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('login.twoFaTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('login.deactivateSecureAccount')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 mb-5">
            <Label htmlFor="disable-2fa-code">{t('login.enterCode')}:</Label>
            <Input
              id="disable-2fa-code"
              inputMode="numeric"
              maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDisable(); }}
              disabled={loading || disableCode.length !== 6}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('login.twoFADisabled')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TwoFactorSection;
